/**
 * TAILWIND HOLD BUTTON KOMPONENTE
 *
 * Button der gedrückt gehalten werden muss. Ideal für destruktive Aktionen.
 * SVG-Progress-Ring, Countdown-Timer, Touch-Overlay mit Haptic Feedback.
 *
 * Props:
 *   onConfirm       - () => void | Promise<void>, wird nach erfolgreichem Halten aufgerufen
 *   holdDuration?   - number (default: 2000ms), Haltezeit in Millisekunden
 *   icon?           - ReactNode, Icon im Button
 *   label?          - string (default: "Löschen"), Text im Normalzustand
 *   text?           - string, Hover-Text (default: automatisch "{n}s halten")
 *   disabled?       - boolean
 *   size?           - number (default: 32), Höhe in px
 *   expandedWidth?  - number, feste Breite in px (sonst automatisch)
 *   alwaysExpanded? - boolean (default: true), Text immer anzeigen
 *   iconOnly?       - boolean (default: false), nur Icon ohne Text
 *   variant?        - dark (default, grauer Hintergrund) | ghost (transparent, roter Border)
 *   className?      - zusätzliche Klassen
 *
 * Usage:
 *   <TWHoldButton
 *     icon={<Trash2 />}
 *     label="Löschen"
 *     onConfirm={() => deleteItem()}
 *     holdDuration={3000}
 *   />
 */

import React, { useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import clsx from 'clsx';

// ============================================
// TYPES
// ============================================
export interface TWHoldButtonProps {
  /** Funktion die nach erfolgreichem Halten ausgeführt wird */
  onConfirm: () => void | Promise<void>;
  /** Haltezeit in Millisekunden (Standard: 2000ms = 2 Sekunden) */
  holdDuration?: number;
  /** Icon das im Button angezeigt wird */
  icon?: React.ReactNode;
  /** Text der standardmäßig angezeigt wird (Standard: "Löschen") */
  label?: string;
  /** Text der beim Hover angezeigt wird - falls nicht gesetzt, automatisch basierend auf holdDuration */
  text?: string;
  /** Zusätzliche CSS-Klassen */
  className?: string;
  /** Button deaktivieren */
  disabled?: boolean;
  /** Höhe des Buttons in px (Standard: 32) */
  size?: number;
  /** Feste Breite des Buttons in px - wenn nicht gesetzt, passt sich automatisch dem Inhalt an */
  expandedWidth?: number;
  /** Text immer anzeigen, nicht nur beim Hover (Standard: true) */
  alwaysExpanded?: boolean;
  /** Nur Icon anzeigen, kein Expandieren (Standard: false) */
  iconOnly?: boolean;
  /** Variante: dark (Standard) oder ghost (transparenter Hintergrund) */
  variant?: 'dark' | 'ghost';
}

// ============================================
// COMPONENT
// ============================================
export function TWHoldButton({
  onConfirm,
  holdDuration = 2000,
  icon,
  label = 'Löschen',
  text,
  className = '',
  disabled = false,
  size = 32,
  expandedWidth: fixedExpandedWidth,
  alwaysExpanded = true,
  iconOnly = false,
  variant = 'dark',
}: TWHoldButtonProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [remainingTime, setRemainingTime] = useState(holdDuration / 1000);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isTouchInteraction, setIsTouchInteraction] = useState(false);
  const [measuredWidth, setMeasuredWidth] = useState(0);

  const startTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const hapticIntervalRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const holdWidthRef = useRef<number>(0);

  // Breite messen via ResizeObserver
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.borderBoxSize?.[0]?.inlineSize ?? entries[0]?.contentRect.width;
      if (width) setMeasuredWidth(width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Haptic Feedback Helper (nur auf unterstützten Geräten)
  const triggerHaptic = useCallback((pattern: number | number[]) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  // Bei iconOnly nie expandieren, bei alwaysExpanded immer, sonst bei hover
  const isExpanded = iconOnly ? false : alwaysExpanded || isHovering;

  const updateProgress = useCallback(() => {
    const elapsed = Date.now() - startTimeRef.current;
    const newProgress = Math.min((elapsed / holdDuration) * 100, 100);
    setProgress(newProgress);

    const remaining = Math.max(0, (holdDuration - elapsed) / 1000);
    setRemainingTime(remaining);

    if (newProgress >= 100) {
      setIsCompleted(true);
      setIsHolding(false);
      setIsTouchInteraction(false);
      triggerHaptic([50, 30, 100]);
      if (hapticIntervalRef.current) {
        clearInterval(hapticIntervalRef.current);
        hapticIntervalRef.current = null;
      }
      onConfirm();
      setTimeout(() => {
        setProgress(0);
        setIsCompleted(false);
      }, 400);
    } else {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  }, [holdDuration, onConfirm, triggerHaptic]);

  const handleStart = useCallback((isTouch = false) => {
    if (disabled) return;

    // Breite einfrieren bevor der Text sich ändert
    if (containerRef.current) {
      holdWidthRef.current = containerRef.current.offsetWidth;
    }

    setIsHolding(true);
    setProgress(0);
    setRemainingTime(holdDuration / 1000);
    startTimeRef.current = Date.now();
    animationFrameRef.current = requestAnimationFrame(updateProgress);

    if (isTouch) {
      setIsTouchInteraction(true);
      triggerHaptic(30);
      hapticIntervalRef.current = window.setInterval(() => {
        triggerHaptic(20);
      }, 500);
    }
  }, [disabled, holdDuration, updateProgress, triggerHaptic]);

  const handleEnd = useCallback(() => {
    setIsHolding(false);
    setIsTouchInteraction(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    if (hapticIntervalRef.current) {
      clearInterval(hapticIntervalRef.current);
      hapticIntervalRef.current = null;
    }
    if (!isCompleted) {
      setProgress(0);
    }
  }, [isCompleted]);

  // Cleanup bei Unmount
  React.useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (hapticIntervalRef.current) {
        clearInterval(hapticIntervalRef.current);
      }
    };
  }, []);

  // Dimensionen
  const borderRadius = size / 2;

  // SVG nutzt die gemessene Breite (oder size als Fallback vor dem ersten Messen)
  const svgRectWidth = measuredWidth || fixedExpandedWidth || size;
  const svgRectHeight = size;
  const strokeWidth = 2;
  const svgPadding = strokeWidth / 2 + 1;
  const svgWidth = svgRectWidth + svgPadding * 2;
  const svgHeight = svgRectHeight + svgPadding * 2;
  const r = Math.min(borderRadius, svgRectWidth / 2, svgRectHeight / 2);

  // SVG Pfad für abgerundetes Rechteck (startet oben Mitte, Uhrzeigersinn)
  const createRoundedRectPath = useMemo(() => {
    const x = svgPadding;
    const y = svgPadding;
    const w = svgRectWidth;
    const h = svgRectHeight;

    return `
      M ${x + w / 2} ${y}
      L ${x + w - r} ${y}
      A ${r} ${r} 0 0 1 ${x + w} ${y + r}
      L ${x + w} ${y + h - r}
      A ${r} ${r} 0 0 1 ${x + w - r} ${y + h}
      L ${x + r} ${y + h}
      A ${r} ${r} 0 0 1 ${x} ${y + h - r}
      L ${x} ${y + r}
      A ${r} ${r} 0 0 1 ${x + r} ${y}
      Z
    `;
  }, [svgPadding, svgRectWidth, svgRectHeight, r]);

  const pathLength = useMemo(() => {
    const straightWidth = Math.max(0, svgRectWidth - 2 * r);
    const straightHeight = Math.max(0, svgRectHeight - 2 * r);
    return 2 * straightWidth + 2 * straightHeight + 2 * Math.PI * r;
  }, [svgRectWidth, svgRectHeight, r]);

  const dashOffset = pathLength * (1 - progress / 100);

  const isGhost = variant === 'ghost';

  // Container-Style: feste Breite wenn explizit gesetzt, sonst auto
  // Während des Haltens wird die Breite eingefroren damit der Button nicht schrumpft
  const containerStyle: React.CSSProperties = {
    height: `${size}px`,
    minWidth: (isHolding || progress > 0) && holdWidthRef.current > 0
      ? `${holdWidthRef.current}px`
      : `${size}px`,
  };
  if (fixedExpandedWidth) {
    containerStyle.width = isExpanded ? `${fixedExpandedWidth}px` : `${size}px`;
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'group relative inline-flex overflow-visible select-none touch-manipulation',
        // Transition nur wenn NICHT gehalten wird - sonst springt min-width sofort
        !(isHolding || progress > 0) && 'transition-all duration-300 ease-out',
        !fixedExpandedWidth && 'w-fit',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className
      )}
      style={{ ...containerStyle, WebkitTouchCallout: 'none', WebkitUserSelect: 'none' }}
      onMouseDown={() => handleStart(false)}
      onMouseUp={handleEnd}
      onMouseLeave={() => {
        handleEnd();
        setIsHovering(false);
      }}
      onMouseEnter={() => setIsHovering(true)}
      onTouchStart={(e) => {
        e.preventDefault();
        handleStart(true);
      }}
      onTouchEnd={handleEnd}
      onContextMenu={(e) => e.preventDefault()}
    >
      <button
        className={clsx(
          // Basis
          'relative flex items-center justify-center w-full h-full p-0',
          'overflow-visible z-[1] select-none touch-manipulation pointer-events-none',
          'transition-all duration-200 ease-out',
          'rounded-full',

          // Disabled
          disabled && 'opacity-50',

          // === DARK VARIANTE ===
          !isGhost && [
            !isHolding && [
              'bg-gray-100 dark:bg-gray-800',
              'border border-gray-200 dark:border-gray-700',
              'group-hover:bg-gray-200 dark:group-hover:bg-gray-700',
            ],
            isHolding && [
              'bg-red-50 dark:bg-red-950/30',
              'border border-red-300 dark:border-red-800',
              'scale-[0.97]',
            ],
          ],

          // === GHOST VARIANTE ===
          isGhost && [
            'border',
            !isHolding && [
              'bg-transparent border-red-200 dark:border-red-900',
              'group-hover:bg-red-50 dark:group-hover:bg-red-950/20',
            ],
            isHolding && [
              'bg-red-50 dark:bg-red-950/30',
              'border-red-300 dark:border-red-800',
              'scale-[0.97]',
            ],
          ],

          // Completed
          isCompleted && 'animate-[holdComplete_0.4s_ease]'
        )}
        style={{ borderRadius: `${borderRadius}px` }}
        disabled={disabled}
        tabIndex={-1}
      >
        {/* SVG Progress Ring */}
        {progress > 0 && measuredWidth > 0 && (
          <svg
            className="absolute pointer-events-none z-10"
            width={svgWidth}
            height={svgHeight}
            style={{
              top: -svgPadding,
              left: -svgPadding,
            }}
          >
            <path
              d={createRoundedRectPath}
              fill="none"
              stroke="rgb(220, 38, 38)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={pathLength}
              strokeDashoffset={dashOffset}
            />
          </svg>
        )}

        {/* Icon */}
        <span
          className={clsx(
            'relative z-[1] flex items-center justify-center shrink-0 transition-all duration-200',
            '[&>svg]:w-3.5 [&>svg]:h-3.5',
            isHolding || isExpanded ? 'text-red-600 dark:text-red-500' : 'text-gray-500 dark:text-gray-400',
            isHolding && 'scale-90'
          )}
          style={{
            width: `${size}px`,
            minWidth: `${size}px`,
          }}
        >
          {icon}
        </span>

        {/* Text */}
        <span
          className={clsx(
            'relative z-[1] whitespace-nowrap text-xs font-semibold transition-all duration-200',
            !isExpanded && 'opacity-0 w-0 overflow-hidden',
            isExpanded && 'opacity-100 pr-4',
            isHolding || isExpanded ? 'text-red-600 dark:text-red-500' : 'text-gray-500 dark:text-gray-400'
          )}
        >
          {isHolding
            ? `${remainingTime.toFixed(1).replace('.', ',')}s`
            : isHovering
              ? (text ?? `${Math.round(holdDuration / 1000)}s halten`)
              : label}
        </span>
      </button>

      {/* Floating Overlay für Touch-Geräte */}
      {isTouchInteraction && isHolding && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 z-50">
          <div
            className={clsx(
              'bg-white dark:bg-gray-800 rounded-lg',
              'shadow-lg dark:shadow-xl',
              'border border-gray-200 dark:border-gray-700',
              'px-4 py-3 min-w-[140px]',
              'animate-[fadeInUp_0.15s_ease-out_forwards]'
            )}
          >
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-white dark:border-t-gray-800" />
            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-px w-0 h-0 border-l-[9px] border-r-[9px] border-t-[9px] border-transparent border-t-gray-200 dark:border-t-transparent -z-10" />

            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-red-500 [&>svg]:w-4 [&>svg]:h-4">
                {icon}
              </span>
              <span className="text-gray-900 dark:text-white font-bold text-lg tabular-nums">
                {remainingTime.toFixed(1).replace('.', ',')}s
              </span>
            </div>

            <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full w-full bg-red-500 rounded-full origin-left"
                style={{ transform: `scaleX(${progress / 100})` }}
              />
            </div>

            <div className="text-gray-500 dark:text-gray-400 text-xs text-center mt-1.5">
              {label}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes holdComplete {
          0% { transform: scale(0.97); }
          50% { transform: scale(1.03); }
          100% { transform: scale(1); }
        }
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

TWHoldButton.displayName = 'TWHoldButton';

export default TWHoldButton;
